import type { CalendarPrefs, HourFormat, WeekStart } from "./calendar-prefs.ts";

export const SUPPORTED_LOCALES: { value: string; label: string }[] = [
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "en-IN", label: "English (India)" },
  { value: "fr-FR", label: "Français (France)" },
  { value: "de-DE", label: "Deutsch (Deutschland)" },
  { value: "es-ES", label: "Español (España)" },
  { value: "pt-BR", label: "Português (Brasil)" },
  { value: "ja-JP", label: "日本語" },
  { value: "zh-CN", label: "中文（简体）" },
];

const DEFAULT_LOCALE = "en-US";

/** Resolves the active locale string for Intl APIs. */
export function activeLocale(prefs: { locale: string | null }): string {
  if (prefs.locale) return prefs.locale;
  if (typeof navigator !== "undefined" && navigator.language) return navigator.language;
  return DEFAULT_LOCALE;
}

/**
 * Format an ISO timestamp's clock time according to the user's locale +
 * 12/24-hour preference. Uses tabular numerals.
 */
export function formatTime(
  iso: string | Date,
  prefs: { locale: string | null; hourFormat: HourFormat },
): string {
  const d = iso instanceof Date ? iso : new Date(iso);
  const hour12 = prefs.hourFormat === "12";
  return d.toLocaleTimeString(activeLocale(prefs), {
    hour: "numeric",
    minute: "2-digit",
    hour12,
  });
}

/**
 * Format a date in the user's locale. Defaults to long form
 * (weekday, month, day, year). Pass `opts` to override.
 */
export function formatDate(
  iso: string | Date,
  prefs: { locale: string | null },
  opts: Intl.DateTimeFormatOptions = { weekday: "long", month: "long", day: "numeric", year: "numeric" },
): string {
  const d = iso instanceof Date ? iso : new Date(iso);
  return d.toLocaleDateString(activeLocale(prefs), opts);
}

/** Money formatter using Intl.NumberFormat. */
export function formatCurrency(
  amount: number,
  prefs: { locale: string | null },
  currency: string,
): string {
  return new Intl.NumberFormat(activeLocale(prefs), { style: "currency", currency }).format(amount);
}

/**
 * Locale-aware week-start. Falls back to the prefs override if set.
 * Returns 0=Sun, 1=Mon, 6=Sat.
 */
export function weekStartForLocale(prefs: { locale: string | null; weekStart?: WeekStart }): WeekStart {
  if (prefs.weekStart !== undefined) return prefs.weekStart;
  // Heuristic: en-US/CA/JP=Sun, most of EU/IN=Mon, MENA=Sat. Without
  // Intl.Locale.weekInfo (not yet universal), this short list is good enough.
  const locale = activeLocale(prefs);
  if (locale.startsWith("en-US") || locale.startsWith("en-CA") || locale.startsWith("ja")) return 0;
  if (locale.startsWith("ar") || locale.startsWith("he")) return 6;
  return 1;
}

/** Build a 7-day list anchored to the given date and week-start preference. */
export function getWeekDaysFor(date: Date, weekStart: WeekStart): Date[] {
  const start = new Date(date);
  const day = start.getDay();
  const diff = (day - weekStart + 7) % 7;
  start.setDate(start.getDate() - diff);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

/** Convenience: pick the slice of CalendarPrefs the formatters care about. */
export type FormatPrefs = Pick<CalendarPrefs, "locale" | "hourFormat" | "weekStart">;
